import React, { useState, useEffect, useContext } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
  Collapse,
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import MenuService from '../../service/menu.service';
import OrderService from '../../service/order.service';
import CouponService from '../../service/coupon.service';
import TableService from '../../service/table.service';
import { formatCentsToBRL, parseBRLToCents, getActiveSizes, formatItemPrice, itemHasMultipleSizes, formatVariants } from '../../utils/currency';
import { normalizePhone } from '../../utils/phone';
import { useNotification } from '../../contexts/NotificationContext';
import { ApiContext } from '../../contexts/ApiContext';
import { accountPath } from '../../utils/accountPath';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal/ConfirmDeleteModal';

const MAX_VARIANT_QUANTITY = 10;

const PRIMARY = '#7c3aed';

const stepperButtonStyle = (disabled) => ({
  width: 32,
  height: 32,
  border: 0,
  borderRadius: '50%',
  background: 'transparent',
  color: PRIMARY,
  fontSize: 18,
  fontWeight: 700,
  lineHeight: 1,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.3 : 1,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
});

const renderStepper = (value, onChange, { min = 0, max = 99, label }) => (
  <div
    className="d-inline-flex align-items-center"
    style={{
      flexShrink: 0,
      border: `1px solid ${value > min ? 'rgba(124,58,237,0.35)' : '#e3e3e3'}`,
      borderRadius: 999,
      background: '#fff',
      padding: 2,
    }}
  >
    <button
      type="button"
      aria-label={`Diminuir ${label}`}
      disabled={value <= min}
      onClick={() => onChange(Math.max(min, value - 1))}
      style={stepperButtonStyle(value <= min)}
    >
      −
    </button>
    <span style={{ minWidth: 24, textAlign: 'center', fontWeight: 700, fontSize: 14, color: '#344675' }}>
      {value}
    </span>
    <button
      type="button"
      aria-label={`Aumentar ${label}`}
      disabled={value >= max}
      onClick={() => onChange(Math.min(max, value + 1))}
      style={stepperButtonStyle(value >= max)}
    >
      +
    </button>
  </div>
);

const AddOrder = () => {
  const navigate = useNavigate();
  const { account } = useContext(ApiContext);
  const { notify } = useNotification();
  
  // Menu data
  const [menu, setMenu] = useState([]);
  const [loadingMenu, setLoadingMenu] = useState(false);
  const [openCategories, setOpenCategories] = useState({});
  
  // Order form
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [payment, setPayment] = useState('CASH');
  const [orderType, setOrderType] = useState('DELIVERY');
  const [tableId, setTableId] = useState('');
  const [tables, setTables] = useState([]);
  const [deliveryCents, setDeliveryCents] = useState(0);
  const [deliveryInput, setDeliveryInput] = useState(formatCentsToBRL(0));
  const [orderNote, setOrderNote] = useState('');
  
  // Address
  const [street, setStreet] = useState('');
  const [number, setNumber] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [complement, setComplement] = useState('');
  const [loadingCep, setLoadingCep] = useState(false);
  
  // Cart
  const [cart, setCart] = useState([]);
  
  // UI
  const [saving, setSaving] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [variantQty, setVariantQty] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [itemNote, setItemNote] = useState('');
  const [showConfirmExit, setShowConfirmExit] = useState(false);
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  useEffect(() => {
    if (account?.menuCode) {
      loadMenu();
    }
    if (account?.id && account?.tablesEnabled) {
      TableService.list(account.id).then((data) => setTables(data || [])).catch(() => {});
    }
  }, [account]);

  const loadMenu = async () => {
    if (!account?.menuCode) {
      notify('tr', 'danger', 'Menu code não encontrado', 'icon-alert-circle-exc');
      return;
    }
    
    setLoadingMenu(true);
    try {
      const data = await MenuService.getMenu(account.menuCode);
      setMenu(data || []);
    } catch (err) {
      console.error(err);
      notify('tr', 'danger', 'Erro ao carregar cardápio', 'icon-alert-circle-exc');
    } finally {
      setLoadingMenu(false);
    }
  };

  const handleCepChange = async (value) => {
    // Remove caracteres não numéricos
    const cleanCep = value.replace(/\D/g, '');
    
    // Formata CEP (00000-000)
    let formattedCep = cleanCep;
    if (cleanCep.length > 5) {
      formattedCep = cleanCep.slice(0, 5) + '-' + cleanCep.slice(5, 8);
    }
    
    setZipCode(formattedCep);
    
    // Se tiver 8 dígitos, busca o endereço
    if (cleanCep.length === 8) {
      setLoadingCep(true);
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
        const data = await response.json();
        
        if (data.erro) {
          notify('tr', 'warning', 'CEP não encontrado', 'icon-alert-circle-exc');
        } else {
          setStreet(data.logradouro || '');
          setNeighborhood(data.bairro || '');
          setCity(data.localidade || '');
          setState(data.uf || '');
          setComplement(data.complemento || '');
        }
      } catch (err) {
        console.error('Erro ao buscar CEP:', err);
        notify('tr', 'danger', 'Erro ao buscar CEP', 'icon-alert-circle-exc');
      } finally {
        setLoadingCep(false);
      }
    }
  };

  const calculateSubtotal = () => {
    return cart.reduce((sum, item) => {
      const variantsTotal = item.variants.reduce((s, v) => s + (v.extraCents || 0), 0);
      return sum + (item.unitPriceCents * item.quantity) + (variantsTotal * item.quantity);
    }, 0);
  };

  const discountCents = appliedCoupon?.discountCents || 0;

  const calculateTotal = () => {
    return Math.max(0, calculateSubtotal() - discountCents) + deliveryCents;
  };

  const apiMessage = (err, fallback) => {
    const msg = err?.response?.data?.message;
    if (Array.isArray(msg)) return msg[0] || fallback;
    return msg || fallback;
  };

  const applyCoupon = async (code = couponInput) => {
    const normalized = String(code || '').trim().toUpperCase();
    if (!normalized) {
      notify('tr', 'warning', 'Informe o código do cupom', 'icon-alert-circle-exc');
      return;
    }
    const subtotal = calculateSubtotal();
    if (subtotal <= 0) {
      notify('tr', 'warning', 'Adicione itens antes de aplicar o cupom', 'icon-alert-circle-exc');
      return;
    }
    setApplyingCoupon(true);
    try {
      const data = await CouponService.validate({
        code: normalized,
        subtotalCents: subtotal,
        accountId: account.id,
      });
      setAppliedCoupon(data);
      setCouponInput(data.code);
    } catch (err) {
      setAppliedCoupon(null);
      notify('tr', 'danger', apiMessage(err, 'Cupom inválido'), 'icon-alert-circle-exc');
    } finally {
      setApplyingCoupon(false);
    }
  };

  useEffect(() => {
    if (!appliedCoupon?.code) return;
    CouponService.validate({
      code: appliedCoupon.code,
      subtotalCents: calculateSubtotal(),
      accountId: account.id,
    })
      .then((data) => setAppliedCoupon(data))
      .catch(() => {
        setAppliedCoupon(null);
        notify('tr', 'warning', 'O cupom não vale mais para este pedido', 'icon-alert-circle-exc');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, account?.id]);

  const selectedExtrasCents = (selectedItem?.variants || []).reduce(
    (s, v) => s + (v.extraCents || 0) * (variantQty[v.id] || 0),
    0,
  );
  const selectedLineCents = selectedSize
    ? (selectedSize.priceCents + selectedExtrasCents) * quantity
    : 0;

  const handleAddToCart = () => {
    if (!selectedItem || !selectedSize) return;

    const lineTotalCents = selectedLineCents;

    const cartItem = {
      id: Date.now(),
      menuItemId: selectedItem.id,
      menuItemSizeId: selectedSize.id,
      sizeName: itemHasMultipleSizes(selectedItem) ? selectedSize.name : undefined,
      name: selectedItem.name,
      description: selectedItem.description,
      unitPriceCents: selectedSize.priceCents,
      quantity,
      lineTotalCents,
      note: itemNote,
      variants: (selectedItem.variants || []).flatMap((v) =>
        Array.from({ length: variantQty[v.id] || 0 }, () => ({
          itemVariantId: v.id,
          name: v.name,
          extraCents: v.extraCents,
        })),
      ),
    };

    setCart([...cart, cartItem]);
    clearItemSelection();
  };

  const clearItemSelection = () => {
    setSelectedItem(null);
    setSelectedSize(null);
    setVariantQty({});
    setQuantity(1);
    setItemNote('');
  };

  const handleSelectItem = (menuItem) => {
    const activeSizes = getActiveSizes(menuItem);
    setSelectedItem(menuItem);
    setSelectedSize(!itemHasMultipleSizes(menuItem) ? activeSizes[0] : null);
    setVariantQty({});
    setQuantity(1);
    setItemNote('');
  };

  const handleRemoveFromCart = (cartItemId) => {
    setCart(cart.filter(item => item.id !== cartItemId));
  };

  const setVariantCount = (variantId, qty) => {
    setVariantQty((prev) => ({ ...prev, [variantId]: qty }));
  };

  const toggleCategory = (categoryId) => {
    setOpenCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const hasFormData = () => {
    return (
      customerName.trim() !== '' ||
      customerPhone.trim() !== '' ||
      orderNote.trim() !== '' ||
      cart.length > 0 ||
      street.trim() !== '' ||
      number.trim() !== '' ||
      zipCode.trim() !== ''
    );
  };

  const handleGoBack = () => {
    if (hasFormData()) {
      setShowConfirmExit(true);
    } else {
      navigate(accountPath(account?.id, '/pedidos'));
    }
  };

  const confirmExit = () => {
    setShowConfirmExit(false);
    navigate(accountPath(account?.id, '/pedidos'));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isDineIn = orderType === 'DINE_IN';

    if (!isDineIn && !customerName.trim()) {
      notify('tr', 'warning', 'Nome do cliente é obrigatório', 'icon-alert-circle-exc');
      return;
    }
    if (!isDineIn && !customerPhone.trim()) {
      notify('tr', 'warning', 'Telefone/WhatsApp do cliente é obrigatório', 'icon-alert-circle-exc');
      return;
    }
    if (cart.length === 0) {
      notify('tr', 'warning', 'Adicione pelo menos um item ao pedido', 'icon-alert-circle-exc');
      return;
    }

    setSaving(true);
    try {
      const selectedTable = isDineIn && tableId
        ? tables.find((t) => String(t.id) === String(tableId))
        : null;

      const orderData = {
        accountId: account.id,
        customerName: customerName.trim() || (selectedTable ? selectedTable.name : ''),
        customerPhone: normalizePhone(customerPhone),
        payment,
        type: orderType,
        tableId: selectedTable ? selectedTable.id : null,
        tableName: selectedTable ? selectedTable.name : null,
        subtotalCents: calculateSubtotal(),
        deliveryCents: orderType === 'DELIVERY' ? deliveryCents : 0,
        totalCents: calculateTotal(),
        couponCode: appliedCoupon?.code || undefined,
        note: orderNote.trim() || undefined,
        items: cart.map(item => ({
          menuItemId: item.menuItemId,
          menuItemSizeId: item.menuItemSizeId,
          sizeName: item.sizeName,
          name: item.name,
          description: item.description,
          unitPriceCents: item.unitPriceCents,
          quantity: item.quantity,
          note: item.note,
          variants: item.variants,
        })),
        address: orderType === 'DELIVERY' && street.trim() ? {
          street: street.trim(),
          number: number.trim(),
          neighborhood: neighborhood.trim(),
          city: city.trim(),
          state: state.trim(),
          zipCode: zipCode.trim(),
          complement: complement.trim(),
        } : undefined,
      };

      await OrderService.createOrder(orderData);
      notify('tr', 'success', 'Pedido criado com sucesso!', 'icon-check-2');
      navigate(accountPath(account?.id, '/pedidos'));
    } catch (err) {
      console.error(err);
      notify('tr', 'danger', apiMessage(err, 'Erro ao criar pedido'), 'icon-alert-circle-exc');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <div className="d-flex justify-content-between align-items-center">
                <CardTitle tag="h4">Novo Pedido</CardTitle>
                <Button color="secondary" size="sm" onClick={handleGoBack}>
                  <i className="tim-icons icon-minimal-left" /> Voltar
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              <Row>
                {/* Customer Info & Address */}
                <Col lg="4" md="12">
                  <Card className="card-plain">
                    <CardHeader>
                      <CardTitle tag="h5">Informações do Cliente</CardTitle>
                    </CardHeader>
                    <CardBody>
                      <FormGroup>
                        <Label>Nome *</Label>
                        <Input
                          type="text"
                          value={customerName}
                          onChange={(e) => setCustomerName(e.target.value)}
                          placeholder="Nome do cliente"
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>Telefone/WhatsApp *</Label>
                        <Input
                          type="text"
                          value={customerPhone}
                          onChange={(e) => setCustomerPhone(e.target.value)}
                          placeholder="(00) 00000-0000"
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>Forma de Pagamento *</Label>
                        <Input
                          type="select"
                          value={payment}
                          onChange={(e) => setPayment(e.target.value)}
                        >
                          <option value="CASH">Dinheiro</option>
                          <option value="CARD">Cartão</option>
                          <option value="PIX">PIX</option>
                          <option value="ONLINE">Pagamento virtual</option>
                        </Input>
                      </FormGroup>
                      <FormGroup>
                        <Label>Tipo de Pedido *</Label>
                        <Input
                          type="select"
                          value={orderType}
                          onChange={(e) => setOrderType(e.target.value)}
                        >
                          <option value="DELIVERY">Entrega</option>
                          <option value="PICKUP">Retirada</option>
                          <option value="DINE_IN">Mesa</option>
                        </Input>
                      </FormGroup>
                      {orderType === 'DINE_IN' && (
                        <FormGroup>
                          <Label>Mesa</Label>
                          <Input
                            type="select"
                            value={tableId}
                            onChange={(e) => setTableId(e.target.value)}
                          >
                            <option value="">Selecione a mesa</option>
                            {tables.map((t) => (
                              <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                          </Input>
                        </FormGroup>
                      )}
                      {orderType === 'DELIVERY' && (
                        <FormGroup>
                          <Label>Taxa de Entrega</Label>
                          <Input
                            type="text"
                            value={deliveryInput}
                            onChange={(e) => {
                              const cents = parseBRLToCents(e.target.value);
                              setDeliveryCents(cents);
                              setDeliveryInput(formatCentsToBRL(cents));
                            }}
                            placeholder="R$ 0,00"
                          />
                        </FormGroup>
                      )}
                      <FormGroup>
                        <Label>Observação do pedido</Label>
                        <Input
                          type="textarea"
                          rows={3}
                          maxLength={500}
                          value={orderNote}
                          onChange={(e) => setOrderNote(e.target.value.slice(0, 500))}
                          placeholder="Ex.: sem cebola, interfone 21, deixar na portaria"
                        />
                        <small className="text-muted">{orderNote.length}/500</small>
                      </FormGroup>
                    </CardBody>
                  </Card>

                  {orderType === 'DELIVERY' && (
                  <Card className="card-plain">
                    <CardHeader>
                      <CardTitle tag="h5">Endereço de Entrega</CardTitle>
                    </CardHeader>
                    <CardBody>
                      <FormGroup>
                        <Label>CEP *</Label>
                        <Input
                          type="text"
                          value={zipCode}
                          onChange={(e) => handleCepChange(e.target.value)}
                          placeholder="00000-000"
                          maxLength={9}
                          disabled={loadingCep}
                        />
                        {loadingCep && (
                          <small className="text-muted">Buscando endereço...</small>
                        )}
                      </FormGroup>
                      
                      <FormGroup>
                        <Label>Rua</Label>
                        <Input
                          type="text"
                          value={street}
                          onChange={(e) => setStreet(e.target.value)}
                          placeholder="Nome da rua"
                        />
                      </FormGroup>
                      
                      <Row>
                        <Col md={6}>
                          <FormGroup>
                            <Label>Número *</Label>
                            <Input
                              type="text"
                              value={number}
                              onChange={(e) => setNumber(e.target.value)}
                              placeholder="123"
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label>Complemento</Label>
                            <Input
                              type="text"
                              value={complement}
                              onChange={(e) => setComplement(e.target.value)}
                              placeholder="Apto 101"
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      
                      <FormGroup>
                        <Label>Bairro</Label>
                        <Input
                          type="text"
                          value={neighborhood}
                          onChange={(e) => setNeighborhood(e.target.value)}
                          placeholder="Nome do bairro"
                        />
                      </FormGroup>
                      
                      <Row>
                        <Col md={8}>
                          <FormGroup>
                            <Label>Cidade</Label>
                            <Input
                              type="text"
                              value={city}
                              onChange={(e) => setCity(e.target.value)}
                              placeholder="São Paulo"
                            />
                          </FormGroup>
                        </Col>
                        <Col md={4}>
                          <FormGroup>
                            <Label>UF</Label>
                            <Input
                              type="text"
                              value={state}
                              onChange={(e) => setState(e.target.value.toUpperCase())}
                              placeholder="SP"
                              maxLength={2}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                  )}
                </Col>

                {/* Menu Items */}
                <Col lg="4" md="6">
                  <Card className="card-plain">
                    <CardHeader>
                      <CardTitle tag="h5">Cardápio</CardTitle>
                    </CardHeader>
                    <CardBody style={{ maxHeight: '600px', overflowY: 'auto' }}>
                      {loadingMenu && <p className="text-muted">Carregando cardápio...</p>}
                      {!loadingMenu && menu.length === 0 && (
                        <p className="text-muted">Nenhum item disponível</p>
                      )}
                      {menu.map((category) => {
                        const activeItems = category.items?.filter(item => item.active) || [];
                        if (activeItems.length === 0) return null;
                        
                        return (
                          <div key={category.id} style={{ marginBottom: 12 }}>
                            <div
                              onClick={() => toggleCategory(category.id)}
                              style={{
                                cursor: 'pointer',
                                padding: '12px 16px',
                                backgroundColor: openCategories[category.id] ? '#1d8cf8' : '#344675',
                                borderRadius: 6,
                                transition: 'all 0.2s',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                color: '#fff',
                              }}
                            >
                              <h6 style={{ fontWeight: 600, margin: 0, color: '#fff' }}>
                                {category.name}
                              </h6>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 12, color: '#fff', opacity: 0.9 }}>
                                  {activeItems.length} {activeItems.length === 1 ? 'item' : 'itens'}
                                </span>
                                <i 
                                  className={`tim-icons ${openCategories[category.id] ? 'icon-minimal-up' : 'icon-minimal-down'}`}
                                  style={{ fontSize: 12 }}
                                />
                              </div>
                            </div>
                            <Collapse isOpen={openCategories[category.id]}>
                              <div style={{ marginTop: 8 }}>
                                {activeItems.map((item) => (
                                  <div
                                    key={item.id}
                                    onClick={() => handleSelectItem(item)}
                                    style={{
                                      cursor: 'pointer',
                                      borderRadius: 6,
                                      transition: 'all 0.2s',
                                      padding: '12px',
                                      marginBottom: 8,
                                      backgroundColor: selectedItem?.id === item.id ? '#1d8cf8' : '#f4f4f4',
                                      color: selectedItem?.id === item.id ? '#fff' : '#344675',
                                    }}
                                  >
                                    <div className="d-flex justify-content-between align-items-start">
                                      <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 600 }}>{item.name}</div>
                                        {item.description && (
                                          <div style={{ 
                                            fontSize: 12, 
                                            marginTop: 4,
                                            opacity: 0.8
                                          }}>
                                            {item.description.substring(0, 60)}
                                            {item.description.length > 60 ? '...' : ''}
                                          </div>
                                        )}
                                      </div>
                                      <div style={{ fontWeight: 600, marginLeft: 10 }}>
                                        {formatItemPrice(item)}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </Collapse>
                          </div>
                        );
                      })}
                    </CardBody>
                  </Card>

                  {selectedItem && (
                    <Card className="card-plain">
                      <CardHeader>
                        <CardTitle tag="h5">Personalizar Item</CardTitle>
                      </CardHeader>
                      <CardBody>
                        <p style={{ fontWeight: 600, marginBottom: 15 }}>{selectedItem.name}</p>

                        {itemHasMultipleSizes(selectedItem) && (
                          <FormGroup>
                            <Label>Medida</Label>
                            {getActiveSizes(selectedItem).map((size) => (
                              <div
                                key={size.id}
                                className="d-flex align-items-center p-2 mb-2 bg-secondary"
                                onClick={() => setSelectedSize(size)}
                                style={{ cursor: 'pointer', borderRadius: 4 }}
                              >
                                <Input
                                  type="radio"
                                  checked={selectedSize?.id === size.id}
                                  onChange={() => {}}
                                  style={{ marginRight: 8 }}
                                />
                                <span>
                                  {size.name} ({formatCentsToBRL(size.priceCents)})
                                </span>
                              </div>
                            ))}
                          </FormGroup>
                        )}
                        
                        <FormGroup className="d-flex align-items-center justify-content-between">
                          <Label className="mb-0">Quantidade</Label>
                          {renderStepper(quantity, setQuantity, { min: 1, label: 'quantidade' })}
                        </FormGroup>

                        {selectedItem.variants && selectedItem.variants.length > 0 && (
                          <FormGroup>
                            <div className="d-flex justify-content-between align-items-baseline mb-2">
                              <Label className="mb-0">Acréscimos</Label>
                              <small className="text-muted">Até {MAX_VARIANT_QUANTITY} de cada</small>
                            </div>
                            {selectedItem.variants.map((variant) => {
                              const qty = variantQty[variant.id] || 0;
                              return (
                                <div
                                  key={variant.id}
                                  className="d-flex align-items-center justify-content-between mb-2"
                                  style={{
                                    padding: '8px 8px 8px 14px',
                                    borderRadius: 8,
                                    border: `1px solid ${qty > 0 ? 'rgba(124,58,237,0.35)' : '#ececf1'}`,
                                    background: qty > 0 ? 'rgba(124,58,237,0.05)' : '#fff',
                                    transition: 'border-color 0.15s, background 0.15s',
                                  }}
                                >
                                  <div style={{ minWidth: 0, marginRight: 12 }}>
                                    <div style={{ fontWeight: 600, fontSize: 14, color: '#344675' }}>{variant.name}</div>
                                    <div style={{ fontSize: 12, color: qty > 0 ? PRIMARY : '#9a9a9a', fontWeight: qty > 0 ? 600 : 400 }}>
                                      +{formatCentsToBRL(variant.extraCents * Math.max(qty, 1))}
                                    </div>
                                  </div>
                                  {renderStepper(qty, (n) => setVariantCount(variant.id, n), {
                                    max: MAX_VARIANT_QUANTITY,
                                    label: variant.name,
                                  })}
                                </div>
                              );
                            })}
                          </FormGroup>
                        )}

                        <FormGroup>
                          <Label>Observação</Label>
                          <Input
                            type="textarea"
                            rows={2}
                            value={itemNote}
                            onChange={(e) => setItemNote(e.target.value)}
                            placeholder="Ex: Sem cebola"
                          />
                        </FormGroup>

                        <div className="d-flex" style={{ gap: 8 }}>
                          <Button 
                            color="secondary" 
                            className="m-0"
                            style={{ flex: 1 }} 
                            onClick={clearItemSelection}
                          >
                            Cancelar
                          </Button>
                          <Button 
                            color="primary" 
                            className="m-0"
                            style={{ flex: 2, whiteSpace: 'nowrap' }} 
                            onClick={handleAddToCart}
                            disabled={!selectedSize}
                          >
                            {selectedSize ? `Adicionar · ${formatCentsToBRL(selectedLineCents)}` : 'Escolha a medida'}
                          </Button>
                        </div>
                      </CardBody>
                    </Card>
                  )}
                </Col>

                {/* Cart */}
                <Col lg="4" md="6">
                  <Card className="card-plain">
                    <CardHeader>
                      <CardTitle tag="h5">
                        Carrinho ({cart.length} {cart.length === 1 ? 'item' : 'itens'})
                      </CardTitle>
                    </CardHeader>
                    <CardBody style={{ maxHeight: '500px', overflowY: 'auto' }}>
                      {cart.length === 0 && (
                        <p className="text-muted text-center py-4">
                          Carrinho vazio. Adicione itens do cardápio.
                        </p>
                      )}
                      {cart.map((item) => (
                        <div key={item.id} className="p-3 mb-2 bg-secondary" style={{ borderRadius: 6 }}>
                          <div className="d-flex justify-content-between align-items-start">
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600 }}>
                                {item.quantity}x {item.name}
                                {item.sizeName && (
                                  <span className="text-muted"> · {item.sizeName}</span>
                                )}
                              </div>
                              {item.variants.length > 0 && (
                                <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
                                  {formatVariants(item.variants)}
                                </div>
                              )}
                              {item.note && (
                                <div className="text-muted" style={{ fontSize: 12, fontStyle: 'italic', marginTop: 4 }}>
                                  Obs: {item.note}
                                </div>
                              )}
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                                {formatCentsToBRL(item.lineTotalCents)}
                              </div>
                              <Button
                                color="danger"
                                size="sm"
                                onClick={() => handleRemoveFromCart(item.id)}
                                style={{ padding: '4px 8px', fontSize: 11 }}
                              >
                                Remover
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </CardBody>
                  </Card>

                  <Card className="card-plain">
                    <CardBody>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Subtotal:</span>
                        <strong>{formatCentsToBRL(calculateSubtotal())}</strong>
                      </div>
                      <FormGroup className="mb-3">
                        <Label>Cupom de desconto</Label>
                        {appliedCoupon ? (
                          <div
                            className="d-flex align-items-center justify-content-between px-3 py-2 rounded"
                            style={{ background: 'rgba(0, 171, 85, 0.12)', border: '1px solid rgba(0, 171, 85, 0.3)' }}
                          >
                            <div>
                              <strong style={{ letterSpacing: 0.6 }}>{appliedCoupon.code}</strong>
                              <div className="text-success" style={{ fontSize: 12 }}>
                                − {formatCentsToBRL(appliedCoupon.discountCents)}
                              </div>
                            </div>
                            <Button
                              color="link"
                              size="sm"
                              className="p-0"
                              onClick={() => {
                                setAppliedCoupon(null);
                                setCouponInput('');
                              }}
                            >
                              Remover
                            </Button>
                          </div>
                        ) : (
                          <div className="d-flex align-items-stretch" style={{ gap: 8 }}>
                            <Input
                              value={couponInput}
                              placeholder="PROMO10"
                              onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  applyCoupon();
                                }
                              }}
                              style={{ letterSpacing: 1, fontWeight: 600 }}
                            />
                            <Button
                              color="primary"
                              className="m-0"
                              onClick={() => applyCoupon()}
                              disabled={applyingCoupon || !couponInput.trim()}
                              style={{ flexShrink: 0, whiteSpace: 'nowrap', padding: '0 18px' }}
                            >
                              {applyingCoupon ? 'Aplicando...' : 'Aplicar'}
                            </Button>
                          </div>
                        )}
                      </FormGroup>
                      {discountCents > 0 && (
                        <div className="d-flex justify-content-between mb-2 text-success">
                          <span>Desconto:</span>
                          <strong>− {formatCentsToBRL(discountCents)}</strong>
                        </div>
                      )}
                      <div className="d-flex justify-content-between mb-2">
                        <span>Entrega:</span>
                        <strong>{formatCentsToBRL(deliveryCents)}</strong>
                      </div>
                      <hr />
                      <div className="d-flex justify-content-between" style={{ fontSize: 18, fontWeight: 700 }}>
                        <span>Total:</span>
                        <span className="text-primary">{formatCentsToBRL(calculateTotal())}</span>
                      </div>
                      
                      <Button
                        color="primary"
                        block
                        size="lg"
                        onClick={handleSubmit}
                        disabled={saving || cart.length === 0}
                        className="mt-4"
                      >
                        {saving ? 'Criando Pedido...' : 'Criar Pedido'}
                      </Button>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <ConfirmDeleteModal
        isOpen={showConfirmExit}
        toggle={() => setShowConfirmExit(false)}
        title="Sair sem salvar?"
        message="Você tem dados não salvos. Deseja realmente sair e perder todas as alterações?"
        btnConfirm="Sair"
        btnCancel="Continuar editando"
        onConfirm={confirmExit}
      />
    </div>
  );
};

export default AddOrder;
